import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from './Toast';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function pad(n) { return String(n).padStart(2, '0'); }

function toLocalDateStr(date) {
  return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}`;
}

function formatTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

export default function CalendarTab({ room, session, onClose, desktop }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);

  const DEV_APPTS = room.id === '00000000-0000-0000-0000-000000000002' ? [
    { id: 'fake-1', room_id: room.id, claim_id: null, name: 'John Smith — State Farm', appt_time: '2026-06-20T10:00:00.000Z', appt_time_end: '2026-06-20T11:30:00.000Z', note: 'Bring estimate docs', claims: null },
  ] : [];

  const [appointments, setAppointments] = useState(DEV_APPTS);
  const [claims, setClaims] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', time_from: '09:00', time_to: '10:00', claim_id: '', note: '' });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    loadAppointments();
    loadClaims();
    const ch = supabase.channel(`appointments-${room.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments', filter: `room_id=eq.${room.id}` }, loadAppointments)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [room.id]);

  async function loadAppointments() {
    if (room.id === '00000000-0000-0000-0000-000000000002') return;
    const { data } = await supabase.from('appointments').select('*, claims(car, plate, client_name)').eq('room_id', room.id).order('appt_time');
    if (data) setAppointments(data);
  }

  async function loadClaims() {
    if (room.id === '00000000-0000-0000-0000-000000000002') return;
    const { data } = await supabase.from('claims').select('id, car, plate, client_name').eq('room_id', room.id).order('updated_at', { ascending: false });
    if (data) setClaims(data);
  }

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length < 42) cells.push(null);

  function apptDot(day) {
    const dateStr = `${viewYear}-${pad(viewMonth+1)}-${pad(day)}`;
    return appointments.some(a => a.appt_time.startsWith(dateStr));
  }

  function apptForDay(dateStr) {
    return appointments.filter(a => a.appt_time.startsWith(dateStr)).sort((a,b) => a.appt_time.localeCompare(b.appt_time));
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y-1); setViewMonth(11); }
    else setViewMonth(m => m-1);
    setSelectedDate(null);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y+1); setViewMonth(0); }
    else setViewMonth(m => m+1);
    setSelectedDate(null);
  }

  function openAddForm() {
    setForm({ name: '', time_from: '09:00', time_to: '10:00', claim_id: '', note: '' });
    setEditId(null);
    setShowForm(true);
  }

  function openEditForm(appt) {
    const d = new Date(appt.appt_time);
    const fromStr = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    let toStr = '10:00';
    if (appt.appt_time_end) {
      const de = new Date(appt.appt_time_end);
      toStr = `${pad(de.getHours())}:${pad(de.getMinutes())}`;
    }
    setForm({ name: appt.name, time_from: fromStr, time_to: toStr, claim_id: appt.claim_id || '', note: appt.note || '' });
    setEditId(appt.id);
    setShowForm(true);
  }

  async function saveAppt() {
    if (!form.name.trim() || !selectedDate) return;

    const [fhh, fmm] = form.time_from.split(':');
    const dtFrom = new Date(selectedDate);
    dtFrom.setHours(parseInt(fhh), parseInt(fmm), 0, 0);

    const [thh, tmm] = form.time_to.split(':');
    const dtTo = new Date(selectedDate);
    dtTo.setHours(parseInt(thh), parseInt(tmm), 0, 0);

    const payload = {
      room_id: room.id,
      name: form.name.trim(),
      appt_time: dtFrom.toISOString(),
      appt_time_end: dtTo.toISOString(),
      claim_id: form.claim_id || null,
      note: form.note.trim() || null,
    };

    // Optimistic: show the appointment and close the form immediately;
    // the realtime subscription reconciles the true state afterwards.
    const byTime = (a, b) => new Date(a.appt_time) - new Date(b.appt_time);
    const joined = form.claim_id ? (claims.find(c => c.id === form.claim_id) || null) : null;
    const wasEdit = editId;
    setShowForm(false);
    setEditId(null);

    if (wasEdit) {
      const prev = appointments;
      setAppointments(a => a.map(x => (x.id === wasEdit ? { ...x, ...payload, claims: joined } : x)).sort(byTime));
      const { error } = await supabase.from('appointments').update(payload).eq('id', wasEdit);
      if (error) {
        setAppointments(prev);
        setEditId(wasEdit);
        setShowForm(true);
        toast('Could not save the appointment — check your connection and try again.', 'error');
      }
    } else {
      const temp = { ...payload, id: `temp-${Date.now()}`, claims: joined };
      setAppointments(a => [...a, temp].sort(byTime));
      const { data, error } = await supabase.from('appointments').insert(payload).select('*, claims(car, plate, client_name)').single();
      if (error) {
        setAppointments(a => a.filter(x => x.id !== temp.id));
        setShowForm(true);
        toast('Could not save the appointment — check your connection and try again.', 'error');
        return;
      }
      setAppointments(a => a.map(x => (x.id === temp.id ? data : x)));
    }
  }

  async function deleteAppt(id) {
    if (!window.confirm('Delete this appointment?')) return;
    const removed = appointments.find(a => a.id === id);
    // Optimistic: remove immediately, restore if the delete fails
    setAppointments(a => a.filter(x => x.id !== id));
    const { error } = await supabase.from('appointments').delete().eq('id', id);
    if (error && removed) {
      setAppointments(a => [...a, removed].sort((x, y) => new Date(x.appt_time) - new Date(y.appt_time)));
      toast('Could not delete the appointment — check your connection and try again.', 'error');
    }
  }

  const selectedDateStr = selectedDate ? toLocalDateStr(selectedDate) : null;
  const dayAppts = selectedDateStr ? apptForDay(selectedDateStr) : [];
  const todayStr = toLocalDateStr(today);

  return (
    <>
    <div className="modal-overlay" style={{ zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: desktop ? 32 : 0 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,6,0.75)' }} onClick={onClose} />
      <div className="modal-panel" style={{ position: 'relative', zIndex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', maxWidth: 1100, maxHeight: desktop ? '88vh' : undefined }}>

        {/* Header */}
        <div style={{ boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', height: 72, padding: '0 20px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 26 : 19, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>Calendar</div>
            <div style={{ flex: 1 }} />
            <button onClick={onClose} className="raise-3d" style={{ background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 20px' }}>
            <button onClick={prevMonth} className="raise-3d" style={{ background: 'var(--bg3)', border: 'none', color: 'var(--text)', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', flex: 1, textAlign: 'center' }}>
              {MONTHS[viewMonth]} {viewYear}
            </div>
            <button onClick={nextMonth} className="raise-3d" style={{ background: 'var(--bg3)', border: 'none', color: 'var(--text)', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
          </div>
        </div>

        {/* Calendar grid + day panel — side by side on desktop, stacked on mobile */}
        <div style={{ display: 'flex', flexDirection: desktop ? 'row' : 'column', flex: 1, minHeight: 0 }}>

          {/* Calendar grid */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: desktop ? '1px solid var(--border)' : 'none', borderBottom: desktop ? 'none' : '1px solid var(--border)', minWidth: 0 }}>
            {/* Day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              {DAYS.map(d => (
                <div key={d} style={{ textAlign: 'center', padding: desktop ? '10px 0' : '6px 0', fontSize: desktop ? 12 : 10, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '1px', color: 'var(--text3)', textTransform: 'uppercase' }}>{d}</div>
              ))}
            </div>
            {/* Grid cells — always 6 rows */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: `repeat(6, ${desktop ? '100px' : '64px'})`, height: desktop ? 'calc(6 * 100px)' : 'calc(6 * 64px)' }}>
              {cells.map((day, i) => {
                if (!day) return <div key={`e-${i}`} style={{ borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }} />;
                const dateStr = `${viewYear}-${pad(viewMonth+1)}-${pad(day)}`;
                const isToday = dateStr === todayStr;
                const isSelected = selectedDateStr === dateStr;
                const hasDot = apptDot(day);
                return (
                  <div key={day} onClick={() => setSelectedDate(new Date(viewYear, viewMonth, day))}
                    style={{
                      borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
                      cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                      background: isSelected ? 'rgba(249,115,22,0.12)' : 'transparent',
                      transition: 'background 0.1s',
                    }}>
                    <div style={{
                      width: desktop ? 42 : 34, height: desktop ? 42 : 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isToday ? 'var(--orange)' : 'transparent',
                      color: isToday ? '#fff' : isSelected ? 'var(--orange)' : 'var(--text)',
                      fontWeight: isToday || isSelected ? 700 : 400,
                      fontSize: desktop ? 20 : 16,
                    }}>{day}</div>
                    {hasDot && <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--orange)' }} />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Day panel */}
          <div style={{ width: desktop ? 300 : '100%', flex: desktop ? 'none' : 1, display: 'flex', flexDirection: 'column', flexShrink: 0, minHeight: 0 }}>
            {selectedDate ? (
              <>
                <div style={{ padding: '12px 16px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text2)' }}>
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  <button onClick={openAddForm} className="raise-3d-orange" style={{ background: 'var(--orange)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '0.5px', cursor: 'pointer' }}>+ ADD</button>
                </div>
                <div className="modal-scroll" style={{ flex: 1, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dayAppts.length === 0 && (
                    <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', paddingTop: 24 }}>No appointments</div>
                  )}
                  {dayAppts.map(appt => {
                    const timeFrom = formatTime(appt.appt_time);
                    const timeTo = appt.appt_time_end ? formatTime(appt.appt_time_end) : null;
                    const claimLabel = appt.claims ? `${appt.claims.car || ''} ${appt.claims.plate || ''}`.trim() : null;
                    return (
                      <div key={appt.id} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 2 }}>{appt.name}</div>
                            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--orange)', marginBottom: claimLabel || appt.note ? 4 : 0 }}>
                              {timeFrom}{timeTo ? ` — ${timeTo}` : ''}
                            </div>
                            {claimLabel && <div style={{ fontSize: 12, color: 'var(--blue)', marginBottom: appt.note ? 2 : 0 }}>🚗 {claimLabel}</div>}
                            {appt.note && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{appt.note}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            <button onClick={() => openEditForm(appt)} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 14, cursor: 'pointer', padding: 2 }}>✏️</button>
                            <button onClick={() => deleteAppt(appt.id)} style={{ background: 'none', border: 'none', color: 'var(--red)', fontSize: 14, cursor: 'pointer', padding: 2 }}>🗑</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 13, padding: 20, textAlign: 'center' }}>
                {desktop ? 'Select a day to view or add appointments' : 'Tap a day above'}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>

    {/* Add/Edit form overlay — rendered as a sibling of the calendar panel,
        not nested inside it: the panel permanently carries a transform
        (animation fill-mode: both), which would make it a containing
        block for this form's fixed positioning on mobile. */}
    {showForm && (
        <div className="modal-overlay" style={{ zIndex: 310, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: desktop ? 32 : 0 }}
          onClick={e => { if (e.target === e.currentTarget) { setShowForm(false); setEditId(null); } }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,5,6,0.8)' }} onClick={() => { setShowForm(false); setEditId(null); }} />
          <div className="modal-panel" style={{ position: 'relative', zIndex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', maxWidth: 400, maxHeight: desktop ? '86vh' : undefined }}>
            <div className="modal-scroll" style={{ flex: 1, padding: desktop ? 24 : 20 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 20 }}>
                {editId ? 'Edit Appointment' : 'New Appointment'}
              </div>
              <div className="field">
                <label className="label">Name</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Appraiser / visitor name" autoFocus />
              </div>
              <div className="field">
                <label className="label">Time</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="time" value={form.time_from} onChange={e => setForm(f => ({ ...f, time_from: e.target.value }))} style={{ flex: 1 }} />
                  <span style={{ color: 'var(--text3)', fontSize: 13, flexShrink: 0 }}>to</span>
                  <input type="time" value={form.time_to} onChange={e => setForm(f => ({ ...f, time_to: e.target.value }))} style={{ flex: 1 }} />
                </div>
              </div>
              <div className="field">
                <label className="label">Linked Claim (optional)</label>
                <select value={form.claim_id} onChange={e => setForm(f => ({ ...f, claim_id: e.target.value }))}>
                  <option value="">— None —</option>
                  {claims.map(c => (
                    <option key={c.id} value={c.id}>{c.car || ''} {c.plate || ''} {c.client_name ? `· ${c.client_name}` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="label">Note (optional)</label>
                <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Short note..." />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button onClick={() => { setShowForm(false); setEditId(null); }} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
                <button onClick={saveAppt} disabled={saving || !form.name.trim()} className="btn btn-primary" style={{ flex: 1 }}>
                  {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
